import styled from 'styled-components'
import SignInContainer from '@ui/sign-in-container/SignInContainer'
import React, {useState} from 'react'
import {NavLink, useLocation, useNavigate} from 'react-router-dom'
import PasswordResetForm from '~/src/components/auth/PasswordResetForm'
import {useGlobalSnackbar} from '~/src/context/GlobalSnackbarContext'
import {BASE_ROUTES} from '~/src/routes/constants'
import {userApi} from '~/src/services/userApi'
import {useSigninMutation, useTemporaryPasswordResetMutation} from '~/src/store/apis/auth-api'
import {extractUserFromToken} from '~/src/utils/jwt-utils'
import EulaDialog from './EulaDialog'

const FormTitle = styled('h2')`
  margin: 0 0 var(--space-2);
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: var(--fw-bold);
  letter-spacing: -0.025em;
`

const FormEmail = styled('div')`
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  color: var(--text-secondary);
  margin: 0 0 var(--space-6);
`

export default function PasswordResetPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const {showError} = useGlobalSnackbar()
  // SHR-FE pattern: Get email and token from location state
  // This page handles temporary password reset from email link
  const email = location?.state?.email
  const token = location?.state?.token

  const [temporaryReset, {error: temporaryError}] = useTemporaryPasswordResetMutation()
  const [signin] = useSigninMutation()
  const [isLoading, setIsLoading] = useState(false)

  // SHR-FE EULA logic
  const [eulaResponse, setEulaResponse] = useState<boolean>(false)
  const [eulaModalVisible, setEulaModalVisible] = useState(true)

  const updateEulaResponse = (response: boolean) => {
    setEulaResponse(response)
    setEulaModalVisible(false)
  }

  // SHR-FE handleSubmit pattern - uses temporaryPasswordReset then login
  const handleSubmit = async (values: {password: string; confirm: string}) => {
    if (eulaResponse) {
      try {
        setIsLoading(true)

        // Step 1: Reset password with temporary token
        await temporaryReset({
          email: email,
          token: token,
          newPassword: values.password,
        }).unwrap()

        // Step 2: Login with new password to get valid token (SHR-FE pattern)
        const loginResponse = await signin({
          emailAddress: email,
          password: values.password,
        }).unwrap()

        // Step 3: Store the new token in localStorage so userApi (BaseApi) has access to it
        // BaseApi fallback looks for 'authToken' key
        let isCandidate = false
        if (loginResponse?.idToken) {
          localStorage.setItem('authToken', loginResponse.idToken)

          const userInfo = extractUserFromToken(loginResponse.idToken)
          isCandidate = userInfo?.roles?.some((r) => r.toLowerCase() === 'candidate') ?? false

          // Step 4: Update agreed to terms date with valid token
          await userApi.updateAgreedToTermsDate(new Date().toISOString())

          // Step 5: Clear localStorage so user signs in fresh (like SHR-FE logOut)
          localStorage.clear()
        }

        // Navigate to password confirmation like SHR-FE
        navigate(BASE_ROUTES.PASSWORD_CONFIRMATION, {
          state: {
            email: email,
            password: values.password,
            isCandidate,
          },
        })
      } catch (error: unknown) {
        showError(error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <SignInContainer
      customFooter={
        <NavLink className="nav-link" to={BASE_ROUTES.SIGNIN}>
          <div>
            Back to <u>Sign In</u>
          </div>
        </NavLink>
      }>
      <FormTitle>Set Password</FormTitle>
      <FormEmail>Login Email: {email}</FormEmail>
      <PasswordResetForm isError={!!temporaryError} onSubmit={handleSubmit} />
      <EulaDialog
        handleClick={updateEulaResponse}
        isModalOpen={eulaModalVisible}
        onModalClose={() => {
          setEulaModalVisible(false)
        }}
      />
    </SignInContainer>
  )
}
